import React, { useEffect, useState, useContext } from 'react';
import { Container, Table, Button, Spinner, Badge, Modal, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { getPlansPublic, getFeatureDefinitions } from '../services/api';
import { formatPrice, getFeatureLabel, getIconClass } from '../utils/planUtils';
import AuthContext from '../context/AuthContext';
import AppNavbar from '../components/AppNavbar';
import LandingNavbar from '../components/LandingNavbar';

// Each analytics row unlocks at a report tier, which is the plan's own `features.reports` value.
// Rows used to be decided by plan name in the markup, so renaming or adding a plan silently lied.
const REPORT_TIERS = ['none', 'basic', 'standard', 'advanced'];

const STATS_ROWS = [
    { key: 'basicMetrics', icon: 'bi-bar-chart', minimumTier: 'basic' },
    { key: 'trends', icon: 'bi-graph-up', minimumTier: 'standard' },
    { key: 'topPerformers', icon: 'bi-trophy', minimumTier: 'standard' },
    { key: 'growthComparison', icon: 'bi-arrow-up-right-circle', minimumTier: 'standard' },
    { key: 'profitAnalysis', icon: 'bi-cash-coin', minimumTier: 'advanced' },
    { key: 'projections', icon: 'bi-calendar-check', minimumTier: 'advanced' },
    { key: 'retention', icon: 'bi-people', minimumTier: 'advanced' },
    { key: 'peakTimes', icon: 'bi-clock-history', minimumTier: 'advanced' },
];

const planReachesTier = (plan, minimumTier) => (
    REPORT_TIERS.indexOf(plan.features?.reports) >= REPORT_TIERS.indexOf(minimumTier)
);

const PlansComparisonPage = () => {
    const { t, i18n } = useTranslation();
    const { user, refreshBusiness } = useContext(AuthContext);
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [featureDefinitions, setFeatureDefinitions] = useState([]);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'pse'

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const promises = [getPlansPublic(), getFeatureDefinitions()];
                if (user) {
                    promises.push(api.get('/plans/current'));
                }

                const [plansResponse, featuresResponse, currentPlanResponse] = await Promise.all(promises);

                // Sort plans by price
                const sortedPlans = plansResponse.data.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
                setPlans(sortedPlans);
                setFeatureDefinitions(featuresResponse.data);

                if (currentPlanResponse) {
                    setCurrentPlan(currentPlanResponse.data);
                }
            } catch (error) {
                console.error('Failed to fetch data:', error);
                toast.error(t('planManagement.errorLoadingPlans'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, t]);

    const handlePlanChange = async (planId) => {
        setUpdating(true);
        try {
            // Call payment creation endpoint
            let endpoint = '/payments/subscriptions/create';
            if (paymentMethod === 'pse') {
                endpoint = '/payments/preferences/create';
            }

            const response = await api.post(endpoint, { plan_id: planId });

            if (response.data.init_point) {
                // Redirect to Mercado Pago
                window.location.href = response.data.init_point;
            } else {
                toast.error(t('planManagement.errorCreatingSubscription'));
            }
        } catch (error) {
            if (error.response?.data?.warnings) {
                setWarnings(error.response.data.warnings);
                setShowWarningModal(true);
            } else {
                toast.error(error.response?.data?.message || t('planManagement.errorUpdatingPlan'));
            }
        } finally {
            setUpdating(false);
        }
    };



    const getPlanName = (plan) => {
        const key = plan.name === 'business_network' ? 'business_network' : plan.name;
        return t(`planManagement.planNames.${key}`) || plan.name;
    };

    const renderFeatureCell = (plan, definition) => {
        const { feature_key: featureKey, type } = definition;
        if (!plan.features) return <span className="text-muted"><i className="bi bi-dash-lg"></i></span>;

        const value = plan.features[featureKey];

        // Complex types logic (keep existing logic)
        if (type === 'complex' && featureKey === 'max_installments') {
            const maxInstallments = plan.features.max_installments;

            if (maxInstallments === 1) {
                return t('planManagement.featureNames.cash_only') || 'Solo ventas de contado';
            }

            const maxSalesWithInstallments = plan.features.max_sales_with_installments;
            const totalSalesLimit = plan.monthly_sales_limit;

            let salesPart = '';
            if (maxSalesWithInstallments && maxSalesWithInstallments !== -1 && maxSalesWithInstallments !== totalSalesLimit) {
                salesPart = `${maxSalesWithInstallments} ${t('planManagement.featureNames.sales') || 'ventas'}`;
            } else {
                salesPart = 'Todas las ventas';
            }

            let installmentsPart = '';
            if (maxInstallments === -1) {
                installmentsPart = 'cuotas ilimitadas';
            } else {
                installmentsPart = `hasta ${maxInstallments} cuotas`;
            }

            return `${salesPart} con ${installmentsPart}`;
        }

        if (type === 'boolean') {
            return value ?
                <i className="bi bi-check-circle-fill text-success fs-5"></i> :
                <span className="text-muted"><i className="bi bi-x-circle text-danger fs-5"></i></span>;
        }

        if (type === 'text') {
            if (featureKey === 'reports') {
                if (!value || value === 'none') return <span className="text-muted"><i className="bi bi-x-circle text-danger fs-5"></i></span>;
                return t(`planManagement.reportLevels.${value}`);
            }
            if (featureKey === 'roles') {
                const rolesLevel = typeof value === 'string' ? value : 'basic';
                return t(`planManagement.featureNames.roles_${rolesLevel}`);
            }
            if (featureKey === 'receipts') {
                const receiptKey = typeof value === 'string' ? value : 'basic';
                return t(`planManagement.receiptLevels.${receiptKey}`);
            }
            return value;
        }

        return value;
    };

    const handleCtaClick = (plan) => {
        if (user) {
            navigate('/settings/plan');
        } else {
            navigate(`/register?plan=${plan.name}&payment_method=${paymentMethod}`);
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100 bg-light">
            {/* Conditional Navbar */}
            {user ? <AppNavbar /> : <LandingNavbar />}

            <Container className="mb-5 flex-grow-1 mt-5">
                <div className="text-center mb-5">
                    <h1 className="fw-bold">{t('planComparison.title', 'Comparación de Planes')}</h1>
                    <p className="lead text-muted">{t('planComparison.subtitle', 'Encuentra el plan perfecto para tu negocio')}</p>

                    {/* Payment Method Toggle */}
                    <div className="d-flex justify-content-center mb-4 mt-4">
                        <div className="bg-light p-1 rounded-pill d-inline-flex border">
                            <Button
                                variant={paymentMethod === 'card' ? 'primary' : 'light'}
                                className="rounded-pill px-4"
                                onClick={() => setPaymentMethod('card')}
                            >
                                <i className="bi bi-credit-card-fill me-2"></i>
                                Subscription (Card)
                            </Button>
                            <Button
                                variant={paymentMethod === 'pse' ? 'primary' : 'light'}
                                className="rounded-pill px-4"
                                onClick={() => setPaymentMethod('pse')}
                            >
                                <span className="me-2 fw-bold">PSE / Nequi</span>
                                Manual Payment
                            </Button>
                        </div>
                    </div>

                    {paymentMethod === 'card' ? (
                        <Alert variant="info" className="d-inline-block">
                            <i className="bi bi-star-fill me-2 text-warning"></i>
                            <strong>Get 1 month free!</strong> Billing starts next month.
                        </Alert>
                    ) : (
                        <Alert variant="secondary" className="d-inline-block">
                            <i className="bi bi-info-circle-fill me-2"></i>
                            The trial month only applies to card subscriptions.
                        </Alert>
                    )}
                </div>

                <div className="table-responsive shadow-sm rounded bg-white">
                    <Table hover className="mb-0 text-center align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="py-4 text-start ps-4" style={{ width: '25%' }}>{t('planComparison.features', 'Features')}</th>
                                {plans.map(plan => {
                                    const isCurrentPlan = currentPlan && currentPlan.plan_id === plan.id;
                                    const isGrowth = plan.name === 'growth';
                                    const showMostPopular = !user && isGrowth;
                                    const highlightGrowth = !user && isGrowth;

                                    return (
                                        <th key={plan.id} className="pb-4 pt-5 position-relative" style={{ minWidth: '200px' }}>
                                            {showMostPopular && (
                                                <Badge bg="primary" pill className="position-absolute top-0 start-50 translate-middle-x mt-3 shadow-sm px-3 py-2">
                                                    {t('landingPage.pricing.mostPopular')}
                                                </Badge>
                                            )}

                                            <h4 className={`fw-bold mt-3 ${highlightGrowth ? 'text-primary' : ''}`}>
                                                {getPlanName(plan)}
                                            </h4>
                                            <div className="fs-4 fw-bold mb-2">
                                                {formatPrice(plan.price, plan.currency)}
                                                <span className="fs-6 text-muted fw-normal">/mo</span>
                                            </div>

                                            {user ? (
                                                // Tarea 027c: el backend (requireVerifiedEmail) es la fuente de
                                                // verdad; deshabilitar acá es solo UX, evita el viaje al 403.
                                                <Button
                                                    variant={isCurrentPlan ? 'primary' : 'outline-primary'}
                                                    size="sm"
                                                    className="rounded-pill px-4"
                                                    onClick={() => !isCurrentPlan && handlePlanChange(plan.id)}
                                                    disabled={isCurrentPlan || updating || !user.email_verified_at}
                                                    title={!user.email_verified_at ? t('planManagement.verifyEmailToSubscribe') : undefined}
                                                >
                                                    {updating ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                            {t('planManagement.updating')}
                                                        </>
                                                    ) : (
                                                        isCurrentPlan ? t('planManagement.currentPlanBadge') : t('planManagement.changePlanButton')
                                                    )}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant={highlightGrowth ? 'primary' : 'outline-primary'}
                                                    size="sm"
                                                    className="rounded-pill px-4"
                                                    onClick={() => handleCtaClick(plan)}
                                                >
                                                    {t('landingPage.hero.getStarted')}
                                                </Button>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Core Limits */}
                            <tr>
                                <td className="text-start ps-4 fw-bold bg-light" colSpan={plans.length + 1}>{t('planComparison.coreLimits', 'Core Limits')}</td>
                            </tr>
                            <tr>
                                <td className="text-start ps-4"><i className="bi bi-building me-2 text-primary"></i>{t('planManagement.businesses')}</td>
                                {plans.map(plan => (
                                    <td key={plan.id}>{plan.max_businesses}</td>
                                ))}
                            </tr>
                            <tr>
                                <td className="text-start ps-4"><i className="bi bi-people me-2 text-primary"></i>{t('planManagement.usersPerBusiness')}</td>
                                {plans.map(plan => (
                                    <td key={plan.id}>{plan.max_users === -1 ? t('planManagement.unlimited') : plan.max_users}</td>
                                ))}
                            </tr>
                            <tr>
                                <td className="text-start ps-4"><i className="bi bi-box-seam me-2 text-primary"></i>{t('planManagement.products')}</td>
                                {plans.map(plan => (
                                    <td key={plan.id}>{plan.max_products === -1 ? t('planManagement.unlimited') : plan.max_products}</td>
                                ))}
                            </tr>
                            <tr>
                                <td className="text-start ps-4"><i className="bi bi-cart me-2 text-primary"></i>{t('planManagement.featureNames.sales')}</td>
                                {plans.map(plan => (
                                    <td key={plan.id}>{(plan.monthly_sales_limit === null || plan.monthly_sales_limit >= 50000) ? t('planManagement.unlimited') : plan.monthly_sales_limit}</td>
                                ))}
                            </tr>
                            <tr>
                                <td className="text-start ps-4"><i className="bi bi-cart-check me-2 text-primary"></i>{t('planManagement.featureNames.sales_with_installments') || 'Sales with Installments'}</td>
                                {plans.map(plan => (
                                    <td key={plan.id}>
                                        {plan.features && plan.features.max_sales_with_installments !== undefined && plan.features.max_sales_with_installments !== -1
                                            ? plan.features.max_sales_with_installments
                                            : 'All'}
                                    </td>
                                ))}
                            </tr>

                            {/* Features - DYNAMIC */}
                            <tr>
                                <td className="text-start ps-4 fw-bold bg-light" colSpan={plans.length + 1}>{t('planComparison.features', 'Features')}</td>
                            </tr>
                            {featureDefinitions.filter(def => def.category === 'feature').map(def => (
                                <tr key={def.id}>
                                    <td className="text-start ps-4">
                                        <i className={`bi ${getIconClass(def.icon_ref)} me-2 text-primary`}></i>
                                        {getFeatureLabel(def, i18n.language)}
                                    </td>
                                    {plans.map(plan => (
                                        <td key={plan.id}>{renderFeatureCell(plan, def)}</td>
                                    ))}
                                </tr>
                            ))}


                            {/* Statistics & Analysis */}
                            <tr>
                                <td className="text-start ps-4 fw-bold bg-light" colSpan={plans.length + 1}>{t('planComparison.stats.title', 'Estadísticas y Análisis')}</td>
                            </tr>
                            {STATS_ROWS.map(row => (
                                <tr key={row.key}>
                                    <td className="text-start ps-4"><i className={`bi ${row.icon} me-2 text-primary`}></i>{t(`planComparison.stats.${row.key}`)}</td>
                                    {plans.map(plan => (
                                        <td key={plan.id}>
                                            {planReachesTier(plan, row.minimumTier)
                                                ? <i className="bi bi-check-circle-fill text-success fs-5"></i>
                                                : <span className="text-muted"><i className="bi bi-lock text-muted fs-5"></i></span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div >
            </Container >

            {/* Footer */}
            < footer className="bg-dark text-light py-4 mt-auto" >
                <Container className="text-center">
                    <small>{t('landingPage.footer.copyright', { year: new Date().getFullYear() })}</small>
                </Container>
            </footer >

            {/* Warning Modal */}
            < Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} size="lg" >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill text-warning me-2" style={{ fontSize: '24px' }}></i>
                        {t('planManagement.cannotChangePlan')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <p className="mb-3">
                            {t('planManagement.resolveRestrictions')}
                        </p>
                        {warnings.map((warning, index) => (
                            <Alert key={index} variant="danger" className="mb-2">
                                <strong>{warning.type === 'users' ? t('planManagement.users') : t('planManagement.products')}:</strong> {warning.message}
                            </Alert>
                        ))}
                    </Alert>
                    <p className="text-muted small">
                        {t('planManagement.adjustAndRetry')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWarningModal(false)}>
                        {t('planManagement.close')}
                    </Button>
                </Modal.Footer>
            </Modal >
        </div >
    );
};

export default PlansComparisonPage;
